import axios from "axios";
import {
  formatChatForDisplay,
  formatChatForModel,
  generateTitle,
} from "../utils/chat_format_handler";
import { handleAuthError } from "../utils/auth_utils";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  timeout: 120000,
});

let isRedirecting = false;
let csrfTokenInMemory = "";

const getCookieValue = (name) => {
  const parts = document.cookie ? document.cookie.split('; ') : [];
  const match = parts.find((cookie) => cookie.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : '';
};

const getCsrfToken = () => getCookieValue('csrf_token');

const resolveCsrfToken = () => csrfTokenInMemory || getCsrfToken();

API.interceptors.request.use((config) => {
  const method = (config.method || 'get').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrfToken = resolveCsrfToken();
    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
});

API.interceptors.response.use(
  (response) => {
    const headerToken = response?.headers?.['x-csrf-token'];
    if (headerToken) {
      csrfTokenInMemory = headerToken;
    }
    return response;
  },
  (error) => {
    if (!isRedirecting) {
      if (handleAuthError(error)) {
        isRedirecting = true;
        setTimeout(() => {
          isRedirecting = false;
        }, 1000);
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  }
);

const passwordLogin = async (email, password) => {
  try {
    const response = await API.post("/login", { email, password });
    if (response.status === 200) {
      if (response.headers?.['x-csrf-token']) {
        csrfTokenInMemory = response.headers['x-csrf-token'];
      }
      return { success: true, data: response.data.data };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const tokenLogin = async (token) => {
  try {
    const response = await API.get(`/jwt-token-login?token=${token}`, {
      withCredentials: true,
    });
    if (response.status === 200) {
      return { success: true };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const wordpressLogin = async (wpToken) => {
  try {
    const response = await API.get(`/sso-login?token=${wpToken}`, {
      withCredentials: true,
    });
    if (response.status === 200) {
      return { success: true, data: response.data.data };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const logout = async () => {
  try {
    const response = await API.post("/logout");
    if (response.status === 200) {
      return { success: true };
    }
    return { success: false };
  } catch (error) {
    return { success: false };
  }
};

const checkAuth = async () => {
  try {
    const response = await API.get("/check-auth");
    if (response.status === 200) {
      if (response.headers?.['x-csrf-token']) {
        csrfTokenInMemory = response.headers['x-csrf-token'];
      } else if (response.data?.data?.csrf_token) {
        csrfTokenInMemory = response.data.data.csrf_token;
      }
      return {
        success: true,
        data: response.data.data,
      };
    }
    return { success: false };
  } catch (error) {
    return { success: false };
  }
};

const getInitialAppData = async () => {
  try {
    const response = await API.get("/index");
    if (response.status === 200) {
      return {
        success: true,
        data: {
          chat_titles: response.data.data.chat_titles || {
            today: [],
            yesterday: [],
            last_seven_days: [],
            older: [],
          },
          models: response.data.data.models || [],
        },
      };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const getChatById = async (conversationId) => {
  try {
    const response = await API.get(`/chat/${conversationId}`);
    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data,
      };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    if (error.code === "ECONNABORTED") {
      return { success: false, error: "Request timed out. Please try again." };
    }
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const editChatTitle = async (conversationId, newTitle) => {
  try {
    const response = await API.put(`/chat/${conversationId}/title`, {
      title: newTitle,
    });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data,
      };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const deleteChat = async (conversationId) => {
  try {
    const response = await API.delete(`/chat/${conversationId}`);
    if (response.status === 200) {
      return { success: true };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const generateImage = async (prompt, messages = []) => {
  try {
    // Format messages for context
    const messageHistory = messages.map((msg) => ({
      role: msg.role,
      content: msg.content,
      ...(msg.imageUrl && { image_url: msg.imageUrl }),
    }));

    const response = await API.post("/generate-image", {
      prompt,
      messages: messageHistory,
    });

    if (response.status === 200) {
      return {
        success: true,
        data: {
          url: response.data.data.url,
        },
      };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const generatePrompt = async (prompt) => {
  try {
    const response = await API.post("/generate-prompt", { prompt });
    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data,
      };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

export const createStreamingChat = async ({
  model,
  message,
  temperature,
  systemPrompt,
  images = [],
  documents = [],
  document_names = [],
  onStream,
  signal,
}) => {
  try {
    const initialMessages = [
      { role: "user", content: message, images: images },
    ];
    const formattedData = formatChatForModel(
      initialMessages,
      systemPrompt,
      model
    );
    const title = generateTitle(initialMessages, model);

    const csrfToken = resolveCsrfToken();
    const headers = { "Content-Type": "application/json" };
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(`${API.defaults.baseURL}/conversations/new`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({
        model,
        messages: formattedData.messages,
        temperature,
        system_prompt: formattedData.system_prompt,
        documents,
        document_names,
        title: title,
      }),
      signal,
    });

    if (!response.ok) {
      throw new Error("Failed to connect to server");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let messageBuffer = "";
    let accumulatedContent = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      messageBuffer += chunk;

      let parts = messageBuffer.split("\n\n");
      messageBuffer = parts.pop();

      for (const part of parts) {
        if (part.startsWith("data:")) {
          const dataText = part.slice(5).trim();
          if (dataText === "") continue;
          try {
            const data = JSON.parse(dataText);

            if (data.event === "[DONE]") {
              if (accumulatedContent.includes('[URL_CORRECTION_START]') && accumulatedContent.includes('[URL_CORRECTION_END]')) {
                const correctionMatch = accumulatedContent.match(/\[URL_CORRECTION_START\](.*?)\[URL_CORRECTION_END\]/s);
                if (correctionMatch) {
                  console.log('Found final URL correction, applying...');
                  onStream({ type: "url_correction", content: correctionMatch[1] });
                }
              }
              return;
            }

            if (data.conversation_id) {
              onStream({ type: "id", conversationId: data.conversation_id });
            } else if (data.citations) {
              onStream({ type: "citations", content: data.citations });
            } else if (data.content) {
              accumulatedContent += data.content;
              if (accumulatedContent.includes('[URL_CORRECTION_START]') && accumulatedContent.includes('[URL_CORRECTION_END]')) {
                const correctionMatch = accumulatedContent.match(/\[URL_CORRECTION_START\](.*?)\[URL_CORRECTION_END\]/s);
                if (correctionMatch) {
                  console.log('Found URL correction, applying...');
                  onStream({ type: "url_correction", content: correctionMatch[1] });
                  accumulatedContent = correctionMatch[1];
                } else {
                  onStream({ type: "content", content: data.content, cot: data.cot });
                }
              } else {
                onStream({ type: "content", content: data.content, cot: data.cot });
              }
            } else if (data.error) {
              onStream({ type: "error", error: data.error, showToast: data.show_toast });
              throw new Error(data.error);
            }
          } catch (e) {
            console.error("Failed to parse SSE data:", e);
            onStream({ type: "error", error: "Failed to parse response from server" });
            throw e;
          }
        }
      }
    }
  } catch (error) {
    if (error.name === "AbortError" || error.message.includes("aborted")) {
      console.log("Streaming aborted by user.");
      return;
    }
    onStream({
      type: "error",
      error: error.message || "An unexpected error occurred",
    });
    throw error;
  }
};

export const sendStreamingMessage = async ({
  conversationId,
  messages,
  model,
  temperature,
  systemPrompt,
  documents,
  document_names,
  onStream,
  signal,
}) => {
  try {
    const formattedData = formatChatForModel(messages, systemPrompt, model);

    const csrfToken = resolveCsrfToken();
    const headers = { "Content-Type": "application/json" };
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(
      `${API.defaults.baseURL}/chat/${conversationId}`,
      {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          messages: formattedData.messages,
          model,
          temperature,
          system_prompt: formattedData.system_prompt,
          documents,
          document_names,
        }),
        signal,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to connect to server");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let messageBuffer = "";
    let accumulatedContent = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      messageBuffer += chunk;

      let parts = messageBuffer.split("\n\n");
      messageBuffer = parts.pop();

      for (const part of parts) {
        if (part.startsWith("data:")) {
          const dataText = part.slice(5).trim();
          if (dataText === "") continue;
          try {
            const data = JSON.parse(dataText);
            if (data.event === "[DONE]") {
              if (accumulatedContent.includes('[URL_CORRECTION_START]') && accumulatedContent.includes('[URL_CORRECTION_END]')) {
                const correctionMatch = accumulatedContent.match(/\[URL_CORRECTION_START\](.*?)\[URL_CORRECTION_END\]/s);
                if (correctionMatch) {
                  console.log('Found final URL correction, applying...');
                  onStream({ type: "url_correction", content: correctionMatch[1] });
                }
              }
              return;
            }
            if (data.citations) {
              onStream({ type: "citations", content: data.citations });
            } else if (data.content) {
              accumulatedContent += data.content;
              if (accumulatedContent.includes('[URL_CORRECTION_START]') && accumulatedContent.includes('[URL_CORRECTION_END]')) {
                const correctionMatch = accumulatedContent.match(/\[URL_CORRECTION_START\](.*?)\[URL_CORRECTION_END\]/s);
                if (correctionMatch) {
                  console.log('Found URL correction, applying...');
                  onStream({ type: "url_correction", content: correctionMatch[1] });
                  accumulatedContent = correctionMatch[1];
                } else {
                  onStream({ type: "content", content: data.content });
                }
              } else {
                onStream({ type: "content", content: data.content });
              }
            } else if (data.error) {
              onStream({ type: "error", error: data.error });
              throw new Error(data.error);
            }
          } catch (e) {
            console.error("Failed to parse SSE data:", e);
            onStream({ type: "error", error: "Failed to parse response from server" });
            throw e;
          }
        }
      }
    }
  } catch (error) {
    if (error.name === "AbortError" || error.message.includes("aborted")) {
      console.log("Streaming aborted by user.");
      return;
    }
    onStream({
      type: "error",
      error: error.message || "An unexpected error occurred",
    });
    throw error;
  }
};

const stopStream = async (conversationId, completeChatHistory, systemPrompt, model ) => {
  try {
    const formattedData = formatChatForModel(completeChatHistory, systemPrompt, model);
    const csrfToken = resolveCsrfToken();
    const headers = { 'Content-Type': 'application/json' };
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }
    const response = await fetch(`${API.defaults.baseURL}/chat/${conversationId}/stop_stream`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        messages: formattedData.messages,
        system_prompt: formattedData.system_prompt,
      }),
    });
    const result = await response.json();
  } catch (error) {
    console.error("Failed to stop stream:", error);
  }
};

const getDashboardData = async () => {
  try {
    const response = await API.get("/superadmin/dashboard");
    if (response.status === 200) {
      return {
        success: true,
        data: response.data.data,
      };
    }
    return {
      success: false,
      error: response.data.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || "Access denied",
    };
  }
};

const enableModelForAll = async (modelId) => {
  try {
    const response = await API.post("/superadmin/enable-model-for-all", {
      model_id: modelId,
    });
    if (response.status === 200) {
      return { success: true };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Something went wrong",
    };
  }
};

const startRagSync = async () => {
  try {
    const response = await API.post("/superadmin/rag/sync");
    if (response.status === 202 || response.status === 200) {
      return { success: true, data: response.data.data, message: response.data.message };
    }
    return { success: false, error: response.data.message || response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.response?.data?.error || "Failed to start PDF sync",
    };
  }
};

const getRagSyncStatus = async () => {
  try {
    const response = await API.get("/superadmin/rag/sync/status");
    if (response.status === 200) {
      return { success: true, data: response.data.data };
    }
    return { success: false, error: response.data.message || response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || error.response?.data?.error || "Failed to fetch PDF sync status",
    };
  }
};

const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await API.post("/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    if (response.status === 200) {
      return { success: true, data: response.data.data, message: response.data.message };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Unable to change password",
    };
  }
};

const deleteUser = async (userId) => {
  try {
    const response = await API.delete(`/superadmin/users/${userId}`);
    if (response.status === 200) {
      return {
        success: true,
        message: response.data.message,
        data: response.data.data,
      };
    }
    return { success: false, error: response.data.message };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to delete user",
    };
  }
};

const createSuperAdminUser = async ({ name, email, password, role }) => {
  try {
    const response = await API.post("/superadmin/users", {
      name,
      email,
      password,
      role,
    });
    if (response.status === 201) {
      return { success: true, data: response.data.data, message: response.data.message };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Failed to create user",
    };
  }
};

const setSuperAdminUserPassword = async (userId, password, requireChange = true) => {
  try {
    const response = await API.put(`/superadmin/users/${userId}/password`, {
      password,
      require_change: requireChange,
    });
    if (response.status === 200) {
      return { success: true, data: response.data.data, message: response.data.message };
    }
    return { success: false, error: response.data.error };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.error || "Failed to update password",
    };
  }
};

export {
  getInitialAppData,
  getChatById,
  editChatTitle,
  deleteChat,
  generateImage,
  generatePrompt,
  API,
  checkAuth,
  logout,
  getDashboardData,
  enableModelForAll,
  stopStream,
  tokenLogin,
  wordpressLogin,
  passwordLogin,
  changePassword,
  deleteUser,
  createSuperAdminUser,
  setSuperAdminUserPassword,
  startRagSync,
  getRagSyncStatus,
};
